import * as React from "react";
import { Recycle, Package, MapPin, Clock, Heart, DollarSign, Trash2, RotateCcw, CalendarCheck, Check, X, Snowflake, Sun, Leaf, Flower2, Gift, Sparkles } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { cn } from "@/lib/utils.js";
import { useSettings } from "@/contexts/useSettings.js";
import { useDashboard } from "@/contexts/DashboardContext.js";
import { inferSeasons, SEASON_LABELS, type SeasonKey } from "@homethyst/shared/utils";

interface UnusedItem {
  item: { id: string; name: string; photoUrl: string | null; spaceName: string | null; quantity: number; tags: string[]; };
  daysSinceLastUsed: number | null;
  daysSinceCreated: number;
  // When true, daysSinceLastUsed is assumed from the added date (no explicit
  // last-used date was recorded). The user can correct it inline.
  assumedFromCreated?: boolean;
}
interface SpaceStat { spaceId: string | null; spaceName: string | null; totalItems: number; unusedItems: number; }

// Timeline defaults, in months. General declutter flags items unused for 6+
// months; seasonal items get a longer default (12 months) since they're only
// expected to be used once a year — a shorter window would flag every seasonal
// item as clutter. Both are user-adjustable and persisted to household settings
// (see settings.declutterMonths / declutterSeasonalMonths) so they sync across
// devices and members rather than resetting each session.
const DEFAULT_MONTHS = 6;
const DEFAULT_SEASONAL_MONTHS = 12;
const DAYS_PER_MONTH = 30;
const clampMonths = (n: number) => Math.min(120, Math.max(1, Math.round(n) || DEFAULT_MONTHS));

// Relative "last used" presets. Each resolves to an ISO date at click time so
// the stored value reflects when the item was actually last used, not just
// "today" — that accuracy is what makes the unused-item suggestions trustworthy.
const USED_PRESETS: { key: string; label: string; hint: string; daysAgo: number }[] = [
  { key: "today", label: "Today", hint: "Using it now", daysAgo: 0 },
  { key: "week", label: "Last week", hint: "~7 days ago", daysAgo: 7 },
  { key: "month", label: "Last month", hint: "~30 days ago", daysAgo: 30 },
  { key: "season", label: "A season ago", hint: "~90 days ago", daysAgo: 90 },
];

function isoDaysAgo(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().split("T")[0]!;
}

const SEASON_ICON: Record<SeasonKey, React.ComponentType<{ className?: string }>> = {
  winter: Snowflake,
  spring: Flower2,
  summer: Sun,
  fall: Leaf,
  holiday: Gift,
};

export default function DeclutterPage() {
  const [items, setItems] = React.useState<UnusedItem[]>([]);
  const [stats, setStats] = React.useState<SpaceStat[]>([]);
  const { declutterMonths, declutterSeasonalMonths } = useSettings();
  const { refresh: refreshDashboard } = useDashboard();
  const [months, setMonths] = React.useState(declutterMonths);
  const [seasonalMonths, setSeasonalMonths] = React.useState(declutterSeasonalMonths);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [seasonFilter, setSeasonFilter] = React.useState<SeasonKey | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [showDatePicker, setShowDatePicker] = React.useState(false);
  // Which item's inline "set last used" date editor is currently open (by id).
  const [editingLastUsedId, setEditingLastUsedId] = React.useState<string | null>(null);
  const toast = useToast();

  // Sync local state once household settings arrive (dashboard loads async).
  const settingsHydrated = React.useRef(false);
  React.useEffect(() => {
    if (settingsHydrated.current) return;
    settingsHydrated.current = true;
    setMonths(declutterMonths);
    setSeasonalMonths(declutterSeasonalMonths);
  }, [declutterMonths, declutterSeasonalMonths]);

  // When a season is active, use the (longer) seasonal window so seasonal items
  // aren't flagged until a full cycle has passed.
  const effectiveMonths = seasonFilter ? seasonalMonths : months;
  const effectiveDays = effectiveMonths * DAYS_PER_MONTH;

  // Persist a changed window to household settings (debounced), then refresh the
  // dashboard so the value is consistent everywhere and survives reloads.
  const persistTimeline = React.useCallback((patch: { declutterMonths?: number; declutterSeasonalMonths?: number }) => {
    apiFetch("/api/settings/declutter-timeline", { method: "PUT", body: JSON.stringify(patch) })
      .then(() => refreshDashboard())
      .catch(() => toast.show("Couldn't save timeline", "error"));
  }, [refreshDashboard, toast]);

  const commitMonths = (raw: number) => {
    const v = clampMonths(raw);
    setMonths(v);
    if (v !== declutterMonths) persistTimeline({ declutterMonths: v });
  };
  const commitSeasonalMonths = (raw: number) => {
    const v = clampMonths(raw);
    setSeasonalMonths(v);
    if (v !== declutterSeasonalMonths) persistTimeline({ declutterSeasonalMonths: v });
  };

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([
        apiFetch<UnusedItem[]>(`/api/items/unused?days=${effectiveDays}`),
        apiFetch<SpaceStat[]>("/api/spaces/declutter-stats"),
      ]);
      setItems(u);
      setStats(s);
      setSelected(new Set());
    } catch {} finally { setLoading(false); }
  }, [effectiveDays]);

  React.useEffect(() => { load(); }, [load]);

  // Precompute inferred seasons once per item.
  const seasonsById = React.useMemo(() => {
    const map = new Map<string, SeasonKey[]>();
    for (const u of items) map.set(u.item.id, inferSeasons(u.item.name, u.item.tags));
    return map;
  }, [items]);

  // Which seasons are actually present, for the lens chips.
  const presentSeasons = React.useMemo(() => {
    const set = new Set<SeasonKey>();
    for (const list of seasonsById.values()) for (const k of list) set.add(k);
    return (["winter", "spring", "summer", "fall", "holiday"] as SeasonKey[]).filter((k) => set.has(k));
  }, [seasonsById]);

  const visibleItems = React.useMemo(() => {
    if (!seasonFilter) return items;
    return items.filter((u) => (seasonsById.get(u.item.id) ?? []).includes(seasonFilter));
  }, [items, seasonFilter, seasonsById]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allVisibleSelected = visibleItems.length > 0 && visibleItems.every((u) => selected.has(u.item.id));
  const toggleSelectAll = () => {
    setSelected((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        for (const u of visibleItems) next.delete(u.item.id);
        return next;
      }
      const next = new Set(prev);
      for (const u of visibleItems) next.add(u.item.id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  // Single-item declutter action (Keep / Donate / Sell / Toss).
  const declutter = async (itemId: string, action: string) => {
    try {
      await apiFetch(`/api/items/${itemId}/declutter`, { method: "PUT", body: JSON.stringify({ action }) });
      setItems((prev) => prev.filter((u) => u.item.id !== itemId));
      setSelected((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
      toast.show(action === "keep" ? "Marked as used" : `Marked as ${action}`);
    } catch (e: any) { toast.show(e.message, "error"); }
  };

  // Bulk-set the last-used date on all selected items — the core input fix.
  const bulkMarkUsed = async (isoDate: string, label: string) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBusy(true);
    try {
      await apiFetch(`/api/items/bulk`, { method: "PUT", body: JSON.stringify({ ids, lastUsedDate: isoDate }) });
      // Anything now used more recently than the window drops off the list.
      setItems((prev) => prev.filter((u) => !selected.has(u.item.id)));
      setSelected(new Set());
      setShowDatePicker(false);
      toast.show(`Marked ${ids.length} item${ids.length === 1 ? "" : "s"} as used ${label.toLowerCase()}`, "success", {
        label: "Undo",
        onClick: () => bulkUndoMarkUsed(ids),
      });
    } catch (e: any) { toast.show(e.message, "error"); }
    finally { setBusy(false); }
  };

  // Undo restores the "never used" state by clearing the date back to null,
  // then reloads so the items reappear in the review list.
  const bulkUndoMarkUsed = async (ids: string[]) => {
    try {
      await apiFetch(`/api/items/bulk`, { method: "PUT", body: JSON.stringify({ ids, lastUsedDate: null }) });
      toast.show("Undone");
      load();
    } catch (e: any) { toast.show(e.message, "error"); }
  };

  // Correct a single item's last-used date inline. This is the per-item
  // affordance that makes the (otherwise hidden) "you can set this" capability
  // discoverable right where the assumption is shown. Reuses the bulk endpoint
  // with a single id.
  const setItemLastUsed = async (itemId: string, isoDate: string, wasAssumed: boolean) => {
    try {
      await apiFetch(`/api/items/bulk`, { method: "PUT", body: JSON.stringify({ ids: [itemId], lastUsedDate: isoDate }) });
      const daysSince = Math.floor((Date.now() - new Date(isoDate).getTime()) / 86400000);
      // If the corrected date still puts the item inside the window, drop it
      // from the list (matches the server's exclusion boundary, hence <=);
      // otherwise update it in place so the user sees their correction stick.
      if (daysSince <= effectiveDays) {
        setItems((prev) => prev.filter((u) => u.item.id !== itemId));
        setSelected((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
      } else {
        setItems((prev) => prev.map((u) => u.item.id === itemId
          ? { ...u, daysSinceLastUsed: daysSince, assumedFromCreated: false }
          : u));
      }
      // Undo restores the prior state: back to the assumed (null) date if it
      // was an assumption, so we never fabricate — or leaves an explicit prior
      // date untouched only when it truly was assumed. For an explicit change
      // there's no reliable prior value to restore, so only offer undo for the
      // assumed→set case to avoid silently discarding real data.
      if (wasAssumed) {
        toast.show("Last used date set", "success", {
          label: "Undo",
          onClick: () => bulkUndoMarkUsed([itemId]),
        });
      } else {
        toast.show("Last used date updated", "success");
      }
    } catch (e: any) { toast.show(e.message, "error"); }
  };

  const bulkDeclutter = async (action: string) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBusy(true);
    try {
      // The declutter endpoint is single-item; fan out but keep the UI atomic.
      await Promise.all(ids.map((id) => apiFetch(`/api/items/${id}/declutter`, { method: "PUT", body: JSON.stringify({ action }) })));
      setItems((prev) => prev.filter((u) => !selected.has(u.item.id)));
      setSelected(new Set());
      toast.show(`Marked ${ids.length} item${ids.length === 1 ? "" : "s"} as ${action}`);
    } catch (e: any) { toast.show(e.message, "error"); }
    finally { setBusy(false); }
  };

  const totalUnused = stats.reduce((a, s) => a + s.unusedItems, 0);
  const totalItems = stats.reduce((a, s) => a + s.totalItems, 0);
  const selectedCount = selected.size;

  return (
    <div className="space-y-8 pb-28">
      <header>
        <div className="flex items-center gap-2 mb-1"><Recycle className="w-5 h-5 text-primary" /><span className="text-sm font-medium text-primary">Declutter</span></div>
        <h1 className="text-2xl font-semibold text-ink font-headline tracking-[-0.01em]">Decluttering Assistant</h1>
        <p className="text-body mt-1 text-sm">Confirm what you still use, then decide what to let go</p>
        <p className="text-faint mt-1 text-[0.8125rem]">Items without a recorded last-used date are assumed to have last been used when they were added — tap <span className="inline-flex items-center gap-0.5 align-middle text-sage-deep font-medium"><CalendarCheck className="w-3 h-3" />Set last used</span> on any item to correct it.</p>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-clay-border p-4 text-center">
          <p className="text-2xl font-semibold text-ink tabular-nums">{totalItems}</p>
          <p className="text-[0.8125rem] text-faint">Total Items</p>
        </div>
        <div className="bg-white rounded-lg border border-clay-border p-4 text-center">
          <p className="text-2xl font-semibold text-warning tabular-nums">{totalUnused}</p>
          <p className="text-[0.8125rem] text-faint">Potentially Unused</p>
        </div>
        <div className="bg-white rounded-lg border border-clay-border p-4 text-center">
          <p className="text-2xl font-semibold text-primary tabular-nums">{totalItems > 0 ? Math.round(((totalItems - totalUnused) / totalItems) * 100) : 100}%</p>
          <p className="text-[0.8125rem] text-faint">Utilization</p>
        </div>
        <div className="bg-white rounded-lg border border-clay-border p-4 text-center">
          <p className="text-2xl font-semibold text-sage-deep tabular-nums">{items.length}</p>
          <p className="text-[0.8125rem] text-faint">To Review</p>
        </div>
      </div>

      {/* Space breakdown */}
      {stats.length > 0 && (
        <section className="bg-white rounded-lg border border-clay-border p-6">
          <h2 className="text-[1.0625rem] font-semibold text-ink mb-4">By Space</h2>
          <div className="space-y-2">
            {stats.filter((s) => s.spaceName).map((s) => {
              const pct = s.totalItems > 0 ? Math.round((s.unusedItems / s.totalItems) * 100) : 0;
              return (
                <div key={s.spaceId} className="flex items-center gap-3">
                  <span className="text-sm text-body w-32 truncate">{s.spaceName}</span>
                  <div className="flex-1 h-2 bg-clay-muted rounded-pill overflow-hidden">
                    <div className={cn("h-full rounded-pill", pct > 50 ? "bg-warning" : "bg-sage")} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[0.8125rem] text-faint w-20 text-right tabular-nums">{s.unusedItems}/{s.totalItems} unused</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Controls: time filter + select-all */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <label className="flex items-center gap-2">
          <span className="text-sm text-body">Not used in:</span>
          <input type="number" min={1} max={120} value={months}
            disabled={!!seasonFilter}
            onChange={(e) => setMonths(Number(e.target.value))}
            onBlur={(e) => commitMonths(Number(e.target.value))}
            className="w-16 px-2 py-1.5 rounded-sm border border-clay-border text-sm text-body text-center tabular-nums focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 disabled:bg-clay-muted disabled:text-faint disabled:cursor-not-allowed" />
          <span className="text-sm text-body">+ months</span>
          {seasonFilter && <span className="text-[0.8125rem] text-faint">(using seasonal window while filtered)</span>}
        </label>
        {visibleItems.length > 0 && (
          <button onClick={toggleSelectAll}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[0.8125rem] font-medium text-primary hover:bg-amethyst-soft transition-colors">
            <span className={cn("w-4 h-4 rounded-sm border flex items-center justify-center", allVisibleSelected ? "bg-primary border-primary" : "border-clay-border")}>
              {allVisibleSelected && <Check className="w-3 h-3 text-white" />}
            </span>
            {allVisibleSelected ? "Deselect all" : "Select all"}
          </button>
        )}
      </div>

      {/* Seasonal lens */}
      {presentSeasons.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-body flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-sage-deep" />Seasonal:</span>
          {presentSeasons.map((k) => {
            const Icon = SEASON_ICON[k];
            const active = seasonFilter === k;
            return (
              <button key={k} onClick={() => { setSeasonFilter(active ? null : k); clearSelection(); }}
                className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-pill text-[0.8125rem] font-medium transition-colors border",
                  active ? "bg-sage-deep text-white border-sage-deep" : "bg-white text-body border-clay-border hover:border-sage")}>
                <Icon className="w-3.5 h-3.5" />{SEASON_LABELS[k]}
              </button>
            );
          })}
          {seasonFilter && (
            <label className="flex items-center gap-2 ml-1">
              <span className="text-[0.8125rem] text-faint">not used in</span>
              <input type="number" min={1} max={120} value={seasonalMonths}
                onChange={(e) => setSeasonalMonths(Number(e.target.value))}
                onBlur={(e) => commitSeasonalMonths(Number(e.target.value))}
                className="w-14 px-2 py-1 rounded-sm border border-clay-border text-[0.8125rem] text-body text-center tabular-nums focus:outline-none focus:border-sage-deep focus:ring-2 focus:ring-sage-deep/30" />
              <span className="text-[0.8125rem] text-faint">+ months</span>
            </label>
          )}
        </div>
      )}
      {seasonFilter && (
        <p className="text-[0.8125rem] text-faint -mt-4">
          Showing {SEASON_LABELS[seasonFilter].toLowerCase()} items unused for {seasonalMonths}+ months — select them and mark used when they're in season so they stop showing as clutter.
        </p>
      )}

      {/* Items to review */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map((i) => <div key={i} className="h-24 bg-clay-muted rounded-lg animate-pulse" />)}</div>
      ) : visibleItems.length === 0 ? (
        <div className="bg-sage-wash rounded-lg border border-clay-border p-12 text-center">
          <Recycle className="w-12 h-12 text-sage-deep mx-auto mb-3" />
          <p className="text-[1.0625rem] font-semibold text-ink">{seasonFilter ? "No items in this season" : "All clear!"}</p>
          <p className="text-sm text-body mt-1">{seasonFilter ? "Try another season or clear the filter" : "No unused items found for this time period"}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleItems.map(({ item, daysSinceLastUsed, assumedFromCreated }) => {
            const isSelected = selected.has(item.id);
            const itemSeasons = seasonsById.get(item.id) ?? [];
            return (
              <div key={item.id}
                className={cn("bg-white rounded-lg border p-4 transition-colors", isSelected ? "border-primary ring-2 ring-primary/30" : "border-clay-border")}>
                <div className="flex items-start gap-3">
                  <button onClick={() => toggle(item.id)} aria-label={isSelected ? "Deselect item" : "Select item"}
                    className={cn("mt-0.5 w-5 h-5 shrink-0 rounded-sm border flex items-center justify-center transition-colors",
                      isSelected ? "bg-primary border-primary" : "border-clay-border hover:border-primary")}>
                    {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                  </button>
                  {item.photoUrl ? (
                    <img src={item.photoUrl} alt="" loading="lazy" className="w-14 h-14 rounded-md object-cover shrink-0" />
                  ) : (
                    <div className="w-14 h-14 rounded-md bg-amethyst-soft flex items-center justify-center shrink-0">
                      <Package className="w-6 h-6 text-primary" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-ink">{item.name}</p>
                      {itemSeasons.map((k) => {
                        const Icon = SEASON_ICON[k];
                        return (
                          <span key={k} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-pill bg-sage-wash text-sage-deep text-[0.8125rem] font-medium">
                            <Icon className="w-2.5 h-2.5" />{SEASON_LABELS[k]}
                          </span>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[0.8125rem] text-faint flex-wrap">
                      {item.spaceName && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.spaceName}</span>}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {assumedFromCreated
                          ? `Assumed last used ~${daysSinceLastUsed}d ago (when added)`
                          : `Last used ${daysSinceLastUsed}d ago`}
                      </span>
                      {/* Discoverable per-item affordance: makes it obvious the
                          assumed date is editable, not a fixed "never used". */}
                      <button
                        type="button"
                        onClick={() => setEditingLastUsedId((id) => (id === item.id ? null : item.id))}
                        aria-expanded={editingLastUsedId === item.id}
                        className={cn(
                          "inline-flex items-center gap-1 px-2 py-0.5 rounded-pill border text-[0.8125rem] font-medium transition-colors",
                          editingLastUsedId === item.id
                            ? "bg-sage-deep text-white border-sage-deep"
                            : "border-sage text-sage-deep hover:bg-sage-wash",
                        )}
                      >
                        <CalendarCheck className="w-3 h-3" />
                        {assumedFromCreated ? "Set last used" : "Change date"}
                      </button>
                    </div>
                    {editingLastUsedId === item.id && (
                      <div className="flex items-center gap-2 mt-2">
                        <input
                          type="date"
                          max={isoDaysAgo(0)}
                          onChange={(e) => {
                            if (e.target.value) {
                              setItemLastUsed(item.id, e.target.value, !!assumedFromCreated);
                              setEditingLastUsedId(null);
                            }
                          }}
                          className="px-2 py-1 rounded-sm border border-clay-border text-[0.8125rem] text-body focus:outline-none focus:border-sage-deep focus:ring-2 focus:ring-sage-deep/30"
                        />
                        <span className="text-[0.8125rem] text-faint">When did you last use this?</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-3 ml-[92px] flex-wrap">
                  <button onClick={() => declutter(item.id, "keep")}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-sage-wash text-sage-deep text-[0.8125rem] font-medium hover:bg-sage/20 transition-colors">
                    <RotateCcw className="w-3 h-3" />Used today
                  </button>
                  <button onClick={() => declutter(item.id, "donated")}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amethyst-soft text-amethyst-ink text-[0.8125rem] font-medium hover:bg-clay-muted transition-colors">
                    <Heart className="w-3 h-3" />Donate
                  </button>
                  <button onClick={() => declutter(item.id, "sold")}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amethyst-soft text-amethyst-ink text-[0.8125rem] font-medium hover:bg-clay-muted transition-colors">
                    <DollarSign className="w-3 h-3" />Sell
                  </button>
                  <button onClick={() => declutter(item.id, "tossed")}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-error-wash text-error-text text-[0.8125rem] font-medium hover:bg-error/20 transition-colors">
                    <Trash2 className="w-3 h-3" />Toss
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sticky bulk action bar */}
      {selectedCount > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 px-4 pb-4 pointer-events-none">
          <div className="pointer-events-auto max-w-3xl mx-auto bg-white/95 backdrop-blur border border-clay-border rounded-lg shadow-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-sm font-medium text-ink">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-pill bg-primary text-white text-[0.8125rem] tabular-nums">{selectedCount}</span>
                selected
              </div>
              <button onClick={clearSelection} className="p-1 rounded-md text-faint hover:bg-clay-muted transition-colors" aria-label="Clear selection"><X className="w-4 h-4" /></button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[0.8125rem] font-medium text-sage-deep flex items-center gap-1"><CalendarCheck className="w-3.5 h-3.5" />Mark used:</span>
              {USED_PRESETS.map((p) => (
                <button key={p.key} disabled={busy} title={p.hint} onClick={() => bulkMarkUsed(isoDaysAgo(p.daysAgo), p.label)}
                  className="px-3 py-1.5 rounded-md bg-sage-wash text-sage-deep text-[0.8125rem] font-medium hover:bg-sage/20 transition-colors disabled:bg-clay-muted disabled:text-faint disabled:cursor-not-allowed">
                  {p.label}
                </button>
              ))}
              <button disabled={busy} onClick={() => setShowDatePicker((v) => !v)}
                className={cn("px-3 py-1.5 rounded-md text-[0.8125rem] font-medium transition-colors disabled:bg-clay-muted disabled:text-faint disabled:cursor-not-allowed", showDatePicker ? "bg-sage-deep text-white" : "bg-sage-wash text-sage-deep hover:bg-sage/20")}>
                Custom date…
              </button>

              <span className="w-px h-5 bg-clay-border mx-1" />

              <button disabled={busy} onClick={() => bulkDeclutter("donated")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amethyst-soft text-amethyst-ink text-[0.8125rem] font-medium hover:bg-clay-muted transition-colors disabled:bg-clay-muted disabled:text-faint disabled:cursor-not-allowed">
                <Heart className="w-3 h-3" />Donate
              </button>
              <button disabled={busy} onClick={() => bulkDeclutter("sold")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amethyst-soft text-amethyst-ink text-[0.8125rem] font-medium hover:bg-clay-muted transition-colors disabled:bg-clay-muted disabled:text-faint disabled:cursor-not-allowed">
                <DollarSign className="w-3 h-3" />Sell
              </button>
              <button disabled={busy} onClick={() => bulkDeclutter("tossed")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-error-wash text-error-text text-[0.8125rem] font-medium hover:bg-error/20 transition-colors disabled:bg-clay-muted disabled:text-faint disabled:cursor-not-allowed">
                <Trash2 className="w-3 h-3" />Toss
              </button>
            </div>

            {showDatePicker && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-clay-border">
                <input type="date" max={isoDaysAgo(0)}
                  onChange={(e) => { if (e.target.value) bulkMarkUsed(e.target.value, `on ${e.target.value}`); }}
                  className="px-3 py-1.5 rounded-sm border border-clay-border text-sm text-body focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                <span className="text-[0.8125rem] text-faint">Pick the date you last used these items</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
